// Loading a team report blob and its player pages, shared by the report page (TeamReportv2) and the KPI dashboard's
// packet view, so the two never drift in how a report id resolves to a blob.
import { useEffect, useState } from "react";
import type { Team } from "../models/Team";
import type { ExplorerSource } from "../models/Explorer";
import type { PlayerStatsPlayerResponse } from "../models/PlayerStats";
import type { CustomReportState, ExplorerAPI } from "../util/Requests";
import type { TeamReportProps } from "./types";

export type SeasonReportType = "season" | "playoff_season" | "preseason_season" | "L5" | "L10" | "L20";

/** A custom window report's id: the team, the key the request returned, the season (`POR-custom-<key>-2025`). */
export const customReportId = (abbrev: string, key: string, season: number) =>
  `${abbrev}-custom-${key}-${season}`;

/** A report id's kind: `POR-game-0022600103` | `POR-season-2026` | `POR-L10-2026` | `POR-custom-<key>-2026` | ... */
export function parseReportId(reportId: string) {
  const gameId = reportId.split("-game-")[1] || null;
  const customKey = /-custom-([0-9a-f]+)-\d+$/.exec(reportId)?.[1] ?? null;
  const type: SeasonReportType = reportId.includes("-L5-")
    ? "L5"
    : reportId.includes("-L10-")
    ? "L10"
    : reportId.includes("-L20-")
      ? "L20"
      : reportId.includes("-playoff_season-")
        ? "playoff_season"
        : reportId.includes("-preseason_season-")
          ? "preseason_season"
          : "season";
  return { gameId, customKey, type, season: gameId ? null : parseInt(reportId.split("-").pop() ?? "", 10) };
}

/** The season-type the detail drill-downs read: from the id, else the blob's header. */
export function reportSeasonType(reportId: string | null, report: TeamReportProps | null) {
  if (reportId?.includes("-playoff_season-")) return "playoff";
  if (reportId?.includes("-preseason_season-")) return "preseason";
  return report?.overviewPageProps?.headerProps?.season_type;
}

/** The season the drill-downs read; game reports leave it out (their Game filter already scopes the data). */
export function reportSeason(reportId: string | null) {
  if (!reportId || reportId.includes("-game-")) return undefined;
  return reportId.split("-").pop() || undefined;
}

/** How often a page asks after a custom report the cron box is building. */
const CUSTOM_POLL_MS = 5000;

/** A custom report not built yet: queued or building on the cron box, or failed (`detail` says why). */
export interface CustomBuild {
  status: Exclude<CustomReportState, "ready">;
  since?: string;
  detail?: string | null;
}

/** What a page says while a custom report is not built yet. */
export function customBuildText(b: CustomBuild) {
  if (b.status === "failed")
    return `The custom report could not be built${b.detail ? `: ${b.detail}` : "."} Pick the games again to retry.`;
  return b.status === "building"
    ? "Building the custom report: usually about a minute (longer right after a game night). It opens here when ready."
    : "Custom report queued: it starts building within a minute and opens here when ready.";
}

/**
 * The blob for `reportId` (game, season-type or custom window report). The previous blob stays while the next one
 * loads. A custom report the cron box has not built yet is polled until it is, with its state in `building`.
 */
export function useTeamReport(
  api: ExplorerAPI,
  team: Team | null,
  reportId: string | null,
  devSource: ExplorerSource | null = null,
) {
  const [report, setReport] = useState<TeamReportProps | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [building, setBuilding] = useState<CustomBuild | null>(null);
  useEffect(() => {
    if (!team || !reportId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setLoading(true);
    setError(false);
    setBuilding(null);
    const { gameId, customKey, type, season } = parseReportId(reportId);
    const loaded = (r: TeamReportProps) => {
      if (cancelled) return;
      setReport(r);
      setLoadedId(reportId);
      setBuilding(null);
      setLoading(false);
    };
    const failed = (err: unknown) => {
      console.error("Error fetching report:", err);
      if (cancelled) return;
      setError(true);
      setLoading(false);
    };
    const pollCustom = () =>
      api
        .getCustomReport(team, season as number, customKey as string)
        .then((r) => {
          if (cancelled) return;
          if (r.status === "ready" && r.report) return loaded(r.report);
          setBuilding({ status: r.status as CustomBuild["status"], since: r.since, detail: r.detail });
          if (r.status === "failed") {
            setError(true);
            setLoading(false);
          } else {
            timer = setTimeout(pollCustom, CUSTOM_POLL_MS);
          }
        })
        .catch(failed);
    if (customKey) {
      pollCustom();
    } else {
      (gameId
        ? api.getTeamGameReport(team, gameId, devSource)
        : api.getTeamSeasonReport(team, season as number, type, devSource)
      )
        .then(loaded)
        .catch(failed);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [api, team, reportId, devSource]);
  return { report, reportId: loadedId, loading, error, building };
}

/**
 * Player roll-up blobs for the report's rotation, keyed by NBA id. Reports built under the 2026 architecture carry no
 * `players` section; the player pages are stitched from these standalone blobs instead (old-format reports still carry
 * `players` and skip this). A missing blob renders a stub page, never a silent drop. Game and preseason reports have
 * no player pages; `enabled = false` (the packet's Full without players) fetches nothing.
 */
export function usePlayerBlobs(
  api: ExplorerAPI,
  report: TeamReportProps | null,
  reportId: string | null,
  enabled = true,
) {
  const [blobs, setBlobs] = useState<Record<string, PlayerStatsPlayerResponse> | null>(null);
  useEffect(() => {
    setBlobs(null);
    if (!report || !enabled) return;
    const roster = report.overviewPageProps?.playerRotations?.players ?? [];
    const isGameReport = !!reportId?.includes("-game-");
    const isPreseason = !!reportId?.includes("-preseason_season-");
    if (
      (report.players?.length ?? 0) > 0 ||
      roster.length === 0 ||
      isGameReport ||
      (isPreseason && report.previewStatsSeason == null)
    )
      return;
    let cancelled = false;
    Promise.allSettled(roster.map((p) => api.getPlayerStatsPlayer("nba", String(p.playerId)))).then((results) => {
      if (cancelled) return;
      const out: Record<string, PlayerStatsPlayerResponse> = {};
      results.forEach((result, i) => {
        if (result.status === "fulfilled" && result.value?.data?.player) {
          out[String(roster[i].playerId)] = result.value.data;
        }
      });
      setBlobs(out);
    });
    return () => {
      cancelled = true;
    };
  }, [report, reportId, api, enabled]);
  return blobs;
}
